import React, { useEffect, useMemo, useState } from 'react'
import { useTranslations } from 'next-intl'
import { XIcon } from '@phosphor-icons/react/dist/ssr/X'
import { Button } from '@skop-app/lib-front'
import { cx } from 'class-variance-authority'

import { useGridApi, useGridState, useGridViews } from './GridRoot'
import { GRID_TOOLBAR_CONTROL_CLASSNAME } from './toolbarControlStyles'
import { usePreventGridFocusSteal } from './usePreventGridFocusSteal'
import type { GridResetProps } from '../types'

export function GridReset({ className, label, title }: GridResetProps) {
  const t = useTranslations()
  const { api, gridIsReady } = useGridApi()
  const {
    selectedFilters,
    externalFiltersSelected,
    quickSearchValue,
    resetGridState,
  } = useGridState()
  const views = useGridViews()
  const [hasSorting, setHasSorting] = useState(false)

  useEffect(() => {
    if (!gridIsReady) {
      setHasSorting(false)
      return
    }
    if (!api) return

    const updateSorting = () => {
      const columnState =
        typeof api.getColumnState === 'function' ? api.getColumnState() : []
      const isSorted =
        Array.isArray(columnState) &&
        columnState.some(
          (column) => column.sort !== null && column.sort !== undefined,
        )
      setHasSorting(isSorted)
    }

    updateSorting()
    api.addEventListener('sortChanged', updateSorting)
    return () => {
      if (api.isDestroyed?.()) return
      try {
        api.removeEventListener('sortChanged', updateSorting)
      } catch {
        // ignore
      }
    }
  }, [api, gridIsReady])

  const hasActiveFilters = useMemo(() => {
    return (
      Object.keys(selectedFilters).length > 0 ||
      Object.keys(externalFiltersSelected).length > 0 ||
      quickSearchValue.trim().length > 0 ||
      hasSorting
    )
  }, [externalFiltersSelected, hasSorting, quickSearchValue, selectedFilters])

  const preventFocusSteal = usePreventGridFocusSteal()

  if (!hasActiveFilters) return null

  const labelText = label ?? t('Toolbar.resetFilters')
  const titleText = title ?? labelText
  const handleReset = () => {
    resetGridState()
    views?.notifyUserGridViewChange({ externalFilters: {}, filterModel: null })
  }

  return (
    <Button
      variant="action"
      className={cx(GRID_TOOLBAR_CONTROL_CLASSNAME, className)}
      onMouseDown={preventFocusSteal}
      onClick={handleReset}
      disabled={views?.isInteractionBlocked === true}
      title={titleText}
    >
      <XIcon className="size-4" />
      <span>{labelText}</span>
    </Button>
  )
}
